import type { TFunction } from 'i18next'
import type { Meter } from 'src/Libs/flow/advanced-settings/powermeter/types'
import { getStorageName } from 'src/Libs/flow/parsers/device-parsers/get-device-default-name'
import { blockingWeeklySheduleToEmcIdlePeriodDto } from 'src/Libs/flow/parsers/parse-storage-blocking-weekly-schedule'
import type { GeneralSettings, InstallationFormModel, StorageSystem } from '../../forms'
import type { EmcIdlePeriodDto, InstallationConfigPUTDto } from '../Installation-config'
import type { EcmConfigPUTDto, StorageConfigPUTDto } from '../device-configs'
import type { InstallationPOSTorPUTDto, InstallationSynchronizePOSTorPUTDto } from '../installations'
import type { MeterPOSTorPUTDto } from '../sub-devices'

export const getInstallationPOSTorPUTDto = (
  installation: InstallationFormModel,
  needToBeSynchronize: boolean,
  updateAddress: boolean
): InstallationPOSTorPUTDto | InstallationSynchronizePOSTorPUTDto => {
  const { generalSettings, gpsLatitude, gpsLongitude } = installation
  const { timeZone, country, street, houseNumber, location, postalCode } = generalSettings.location
  const requestDTO: InstallationSynchronizePOSTorPUTDto = {
    needToBeSynchronize,
  }
  if (updateAddress) {
    return {
      ...requestDTO,
      projectName: installation.name,
      timezone: timeZone,
      countryCode: country,
      streetName: street,
      streetNumber: houseNumber,
      city: location,
      zip: postalCode,
      gpsLatitude: gpsLatitude,
      gpsLongitude: gpsLongitude,
    }
  }
  return requestDTO
}

export const getInstallationConfigPUTDto = (installation: Partial<InstallationFormModel>): Partial<InstallationConfigPUTDto> => {
  const { storageSystem, generalSettings } = installation
  const generalGridSettings = generalSettings?.gridSettings
  const storageGeneralSettings = storageSystem?.generalSettings
  const storageAdvancedSettings = storageGeneralSettings?.advancedSettings
  const storageSystemRegulation = storageGeneralSettings?.systemRegulation
  const priorityChargingConfiguration = storageGeneralSettings?.chargingConfiguration
  const priorityChargingDevice = priorityChargingConfiguration?.priorityChargingDevice
  const weatherOptimizationOption = storageAdvancedSettings?.weatherForecastOptimization?.weatherOptimizationOption

  return {
    eegValue: (storageSystemRegulation?.feedInRatio as number) ?? undefined,
    eegConnectedPower: (storageSystemRegulation?.feedInMaxPower as number) ?? undefined,
    eegDerateAtConnectedPower: storageSystemRegulation?.hasFeedInLimit,
    emsWeatherRegulatedChargeEnabled: storageAdvancedSettings?.weatherForecastOptimization?.useWeatherForecastBasedCharging,
    emsBatteryBeforeCarMode: priorityChargingDevice && priorityChargingDevice === 'battery',
    emsBatteryToCarMode: priorityChargingConfiguration?.useBatteryDischargeOnlyWhenPvIsExceeded,
    emsWallboxEnforcePowerAssignment: priorityChargingConfiguration?.useBatteryDischargeOnlyInOtherMode,
    peakPowerPv: (storageSystemRegulation?.peakPower as number) ?? undefined,
    phaseCount: (generalGridSettings?.numberOfPhases ? (generalGridSettings?.numberOfPhases === 'single-phase' ? 1 : 3) : undefined) as
      | 1
      | 3
      | undefined,
    mainFuse: (generalGridSettings?.mainProtection as number) ?? undefined,
    mainFuseDerated: (generalGridSettings?.thermalDerating as number) ?? undefined,
    sgrCooldown: weatherOptimizationOption && weatherOptimizationOption === 'Heat pump via SG-Ready',
    emergencyPowerCheckPartialGridFailure: storageAdvancedSettings?.emergencyPower?.useEmergencyPower,
    emergencyPowerDelayInSeconds: (storageAdvancedSettings?.emergencyPower?.emergencyPowerDelay as number) ?? undefined,
    coordinateChargingUseCase: false,
    phaseImbalance: false,
    phaseImbalanceValue: 4.6,
    selfconsumptionUseCase: false,
    rootPowerMeter: storageGeneralSettings?.wiringTopologyDeviceId,
    countryCode: generalSettings?.location?.country,
  }
}

export const getEmcConfigPUTDto = (generalSettings: GeneralSettings): Pick<EcmConfigPUTDto, 'useCT' | 'ratioCT'> => {
  const { electricalCurrentTransformerRatio, hasElectricalCurrentTransformer } = generalSettings.gridSettings.gridMeter

  return {
    useCT: hasElectricalCurrentTransformer,
    ratioCT: electricalCurrentTransformerRatio,
  }
}

export const getStorageConfigPUTDto = (
  storageSpecificSettings: Partial<StorageSystem['storageSpecificSettings'][number]>,
  storageDeviceName: string | undefined,
  t: TFunction
): Partial<StorageConfigPUTDto> => {
  const storageConfigDto: Partial<StorageConfigPUTDto> = {
    pvInverterStringCount: storageSpecificSettings?.chargingConfiguration?.trackersBehaviour
      ? storageSpecificSettings?.chargingConfiguration?.trackersBehaviour === 'single tracker'
        ? 1
        : 2
      : undefined,
    emergencyPowerCapacityReserve: (storageSpecificSettings?.advancedSettings?.emergencyPower?.emergencyPowerReserve as number) ?? undefined,
    name: getStorageName(storageDeviceName, t),
    sgrUsedPower: 0,
    emsOperatingRangeAuto: storageSpecificSettings?.advancedSettings?.batteryOperationRange?.isAuto,
    emsMaxChargePower: (storageSpecificSettings?.advancedSettings?.batteryOperationRange?.maximumChargingPower as number) ?? undefined,
    emsMaxDischargePower: (storageSpecificSettings?.advancedSettings?.batteryOperationRange?.maximumDischargingPower as number) ?? undefined,
    emsDischargeStartPower:
      (storageSpecificSettings?.advancedSettings?.batteryOperationRange?.minimumConsumptionPowerToStartDischarge as number) ?? undefined,
    emsPowersaveEnabled: storageSpecificSettings?.advancedSettings?.useStorageSystemEnergySavingMode,
  }

  return storageConfigDto
}
export const getEmcIdlePeriodsPUTDto = (storageSystem: StorageSystem): EmcIdlePeriodDto[] => {
  const { chargeBlockingWeeklySchedule, dischargeBlockingWeeklySchedule } = storageSystem.generalSettings.advancedSettings

  return [
    ...chargeBlockingWeeklySchedule.map(schedule => blockingWeeklySheduleToEmcIdlePeriodDto(schedule, 'charge')),
    ...dischargeBlockingWeeklySchedule.map(schedule => blockingWeeklySheduleToEmcIdlePeriodDto(schedule, 'discharge')),
  ]
}

export const getMeterPOSTorPUTDto = (meter: Meter): MeterPOSTorPUTDto => {
  return {
    deviceName: meter.name,
    media: 'ModbusRTU',
    mediaParameters: {
      address: parseInt(meter.pairingId),
    },
    wiringMode: meter.wiringMode,
    ...getMeterTypeWithParameters(meter),
  }
}

const getMeterTypeWithParameters = (meter: Meter): Pick<MeterPOSTorPUTDto, 'deviceType' | 'typeParameters'> => {
  const { deviceType } = meter
  switch (meter.deviceType) {
    case 'HVAC':
      return {
        deviceType,
        typeParameters: {
          nominalPower: null,
        },
      }

    case 'PVExtern':
      return {
        deviceType,
        typeParameters: {
          peakPower: meter.peakPower || 2,
        },
      }

    case 'House': {
      return {
        deviceType,
        typeParameters: {},
      }
    }

    case 'Evse': {
      return {
        deviceType,
        typeParameters: {
          wallboxId: null,
        },
      }
    }
  }
}
